import type {
  BattleActorDto,
  BattleEventDto,
  BattleReplayDto,
  BuildingType,
  CityBuildingDto,
  CityStateDto,
  CombatPlanDto,
  FormationSlotDto,
  HeroId,
  NeutralUnitType,
  PlayerStateDto,
  QuestId,
  ResearchId,
  ResourceType,
  ResourceWallet,
  TerrainType,
  TechnologyLevels,
  TileDto,
  TacticType,
  UnitRoster,
  UnitRole,
  UnitType,
} from "@acre/protocol";

export const EMPTY_WALLET: ResourceWallet = { food: 0, wood: 0, stone: 0, coins: 0 };

export const STARTING_RESOURCES: ResourceWallet = {
  food: 600,
  wood: 420,
  stone: 260,
  coins: 100,
};

export const STARTING_UNITS: UnitRoster = {
  militia: 3,
  archer: 1,
  cavalry: 0,
  shield: 1,
  crossbow: 0,
};

export const EMPTY_TECHNOLOGY: TechnologyLevels = {
  crop_rotation: 0,
  frontier_logistics: 0,
  battle_drill: 0,
};

export const STARTING_HEROES: HeroId[] = ["sun-wu", "hua-mulan", "zhao-yun"];

export const UNIT_DEFINITIONS: Record<
  UnitType,
  {
    name: string;
    foodCost: number;
    power: number;
    barracksLevel: number;
    hp: number;
    attack: number;
    range: number;
    speed: number;
    role: UnitRole;
    trait: string;
  }
> = {
  militia: { name: "ทหารหอกยาว", foodCost: 50, power: 1, barracksLevel: 1, hp: 26, attack: 5, range: 1, speed: 5, role: "frontline", trait: "ตั้งแนวต้านทหารม้า" },
  archer: { name: "พรานธนู", foodCost: 90, power: 2, barracksLevel: 2, hp: 18, attack: 7, range: 4, speed: 4, role: "ranged", trait: "ยิงได้ก่อนศัตรูเข้าประชิด" },
  cavalry: { name: "ทหารม้าดาบ", foodCost: 150, power: 4, barracksLevel: 3, hp: 34, attack: 10, range: 1, speed: 8, role: "cavalry", trait: "พุ่งเข้าถึงแนวหลังรวดเร็ว" },
  shield: { name: "ทหารโล่ใหญ่", foodCost: 110, power: 2, barracksLevel: 2, hp: 44, attack: 3, range: 1, speed: 3, role: "frontline", trait: "รับความเสียหายแทนแนวหลัง" },
  crossbow: { name: "พลหน้าไม้", foodCost: 135, power: 3, barracksLevel: 3, hp: 19, attack: 9, range: 5, speed: 2, role: "ranged", trait: "ยิงไกลแรง แต่เดินช้า" },
};

export const HERO_DEFINITIONS: Record<HeroId, {
  name: string;
  title: string;
  skillName: string;
  description: string;
  favoredRole: UnitRole | "all";
  multiplier: number;
}> = {
  "sun-wu": { name: "ซุนวู", title: "ปรมาจารย์พิชัยสงคราม", skillName: "พิชัยไร้รูป", description: "เพิ่มพลังทั้งกอง และโจมตีศัตรูในรอบที่ 2", favoredRole: "all", multiplier: 1.08 },
  "hua-mulan": { name: "ฮวามู่หลาน", title: "แม่ทัพเกราะแดง", skillName: "โล่ดอกเหมย", description: "เสริมกองหน้าและลดแรงปะทะช่วงต้น", favoredRole: "frontline", multiplier: 1.18 },
  "zhao-yun": { name: "จูล่ง", title: "ขุนพลม้าขาว", skillName: "ทะลวงฉางปัน", description: "เพิ่มพลังทหารม้าและเร่งเข้าประชิด", favoredRole: "cavalry", multiplier: 1.2 },
};

export const RESEARCH_DEFINITIONS: Record<ResearchId, {
  name: string;
  description: string;
  baseCost: ResourceWallet;
  baseDurationSeconds: number;
  academyLevel: number;
  maxLevel: number;
}> = {
  crop_rotation: { name: "เกษตรหมุนเวียน", description: "ผลผลิตอาหาร +12% ต่อระดับ", baseCost: { food: 80, wood: 130, stone: 60, coins: 15 }, baseDurationSeconds: 45, academyLevel: 1, maxLevel: 5 },
  frontier_logistics: { name: "เสบียงชายแดน", description: "ผลผลิตจากผืนดิน +10% ต่อระดับ", baseCost: { food: 120, wood: 100, stone: 100, coins: 20 }, baseDurationSeconds: 60, academyLevel: 1, maxLevel: 5 },
  battle_drill: { name: "ฝึกกระบวนทัพ", description: "พลังรบทั้งหมด +10% ต่อระดับ", baseCost: { food: 180, wood: 80, stone: 140, coins: 30 }, baseDurationSeconds: 75, academyLevel: 2, maxLevel: 5 },
};

export const QUEST_DEFINITIONS: Record<QuestId, { name: string; description: string; reward: ResourceWallet }> = {
  raise_barracks: { name: "เตรียมกองทัพ", description: "สร้างค่ายทหาร", reward: { food: 250, wood: 80, stone: 0, coins: 20 } },
  claim_three: { name: "ชายแดนแรก", description: "ครอบครอง 3 ผืนดิน", reward: { food: 180, wood: 180, stone: 100, coins: 25 } },
  found_academy: { name: "ภูมิปัญญาของแคว้น", description: "สร้างสำนักบัณฑิต", reward: { food: 100, wood: 120, stone: 160, coins: 35 } },
  first_research: { name: "ก้าวแรกแห่งวิทยาการ", description: "วิจัยเทคโนโลยีสำเร็จ 1 ระดับ", reward: { food: 200, wood: 100, stone: 100, coins: 40 } },
  claim_six: { name: "อาณาเขตกำเนิด", description: "ครอบครอง 6 ผืนดิน", reward: { food: 350, wood: 250, stone: 180, coins: 60 } },
};

interface BuildingDefinition {
  name: string;
  baseCost: ResourceWallet;
  production: Partial<ResourceWallet>;
  minCapitalLevel: number;
  maxCount: number;
  baseBuildTimeSeconds: number;
}

export const BUILDING_DEFINITIONS: Record<BuildingType, BuildingDefinition> = {
  capital: {
    name: "Capital",
    baseCost: { food: 0, wood: 250, stone: 180, coins: 30 },
    production: { coins: 12 },
    minCapitalLevel: 1,
    maxCount: 1,
    baseBuildTimeSeconds: 90,
  },
  farm: {
    name: "Farm",
    baseCost: { food: 0, wood: 80, stone: 20, coins: 0 },
    production: { food: 180 },
    minCapitalLevel: 1,
    maxCount: 4,
    baseBuildTimeSeconds: 20,
  },
  lumberyard: {
    name: "Lumberyard",
    baseCost: { food: 60, wood: 0, stone: 25, coins: 0 },
    production: { wood: 150 },
    minCapitalLevel: 1,
    maxCount: 4,
    baseBuildTimeSeconds: 25,
  },
  quarry: {
    name: "Quarry",
    baseCost: { food: 80, wood: 65, stone: 0, coins: 0 },
    production: { stone: 110 },
    minCapitalLevel: 1,
    maxCount: 4,
    baseBuildTimeSeconds: 30,
  },
  barn: {
    name: "Barn",
    baseCost: { food: 0, wood: 120, stone: 70, coins: 0 },
    production: {},
    minCapitalLevel: 1,
    maxCount: 1,
    baseBuildTimeSeconds: 40,
  },
  warehouse: {
    name: "Warehouse",
    baseCost: { food: 0, wood: 100, stone: 110, coins: 0 },
    production: {},
    minCapitalLevel: 1,
    maxCount: 1,
    baseBuildTimeSeconds: 45,
  },
  barracks: {
    name: "Barracks",
    baseCost: { food: 0, wood: 160, stone: 110, coins: 20 },
    production: {},
    minCapitalLevel: 1,
    maxCount: 1,
    baseBuildTimeSeconds: 60,
  },
  academy: {
    name: "Academy",
    baseCost: { food: 100, wood: 220, stone: 180, coins: 35 },
    production: {},
    minCapitalLevel: 2,
    maxCount: 1,
    baseBuildTimeSeconds: 90,
  },
};

export const CITY_SIZE = 5;
export const MAX_BUILDING_LEVEL = 10;

export function createStartingBuildings(): CityBuildingDto[] {
  return [
    { id: "capital:2:2", type: "capital", x: 2, y: 2, level: 1 },
    { id: "farm:1:2", type: "farm", x: 1, y: 2, level: 1 },
    { id: "lumberyard:3:2", type: "lumberyard", x: 3, y: 2, level: 1 },
  ];
}

export function getBuildingCost(type: BuildingType, targetLevel: number): ResourceWallet {
  const definition = BUILDING_DEFINITIONS[type];
  const exponent = type === "capital" ? Math.max(0, targetLevel - 2) : Math.max(0, targetLevel - 1);
  const multiplier = 1.55 ** exponent;
  return mapWallet(definition.baseCost, (amount) => Math.ceil((amount * multiplier) / 5) * 5);
}

export function getConstructionDurationMs(type: BuildingType, targetLevel: number): number {
  const baseSeconds = BUILDING_DEFINITIONS[type].baseBuildTimeSeconds;
  const levelMultiplier = 1.6 ** Math.max(0, targetLevel - 1);
  return Math.ceil((baseSeconds * levelMultiplier) / 5) * 5_000;
}

export function getResearchCost(researchId: ResearchId, targetLevel: number): ResourceWallet {
  const definition = RESEARCH_DEFINITIONS[researchId];
  const multiplier = 1.7 ** Math.max(0, targetLevel - 1);
  return mapWallet(definition.baseCost, (amount) => Math.ceil((amount * multiplier) / 5) * 5);
}

export function getResearchDurationMs(researchId: ResearchId, targetLevel: number): number {
  const definition = RESEARCH_DEFINITIONS[researchId];
  const multiplier = 1.65 ** Math.max(0, targetLevel - 1);
  return Math.ceil((definition.baseDurationSeconds * multiplier) / 5) * 5_000;
}

export function getBuildingLevel(buildings: CityBuildingDto[], type: BuildingType): number {
  return buildings.reduce(
    (level, building) => building.type === type ? Math.max(level, building.level) : level,
    0,
  );
}

export function getCityEconomy(
  city: CityStateDto,
  territoryYieldPerHour: ResourceWallet,
  resources: ResourceWallet,
  nowMs: number,
  technology: TechnologyLevels = EMPTY_TECHNOLOGY,
): Pick<CityStateDto, "productionPerHour" | "claimable" | "storageCaps"> {
  const frontierMultiplier = 1 + (technology.frontier_logistics ?? 0) * 0.1;
  const productionPerHour = mapWallet(territoryYieldPerHour, (amount) => Math.floor(amount * frontierMultiplier));
  for (const building of city.buildings) {
    const production = BUILDING_DEFINITIONS[building.type].production;
    for (const resource of resourceTypes()) {
      productionPerHour[resource] += (production[resource] ?? 0) * building.level;
    }
  }
  productionPerHour.food = Math.floor(productionPerHour.food * (1 + (technology.crop_rotation ?? 0) * 0.12));

  const capitalLevel = getBuildingLevel(city.buildings, "capital") || 1;
  const barnLevel = getBuildingLevel(city.buildings, "barn");
  const warehouseLevel = getBuildingLevel(city.buildings, "warehouse");
  const storageCaps: ResourceWallet = {
    food: 1_500 + capitalLevel * 500 + barnLevel * 2_500,
    wood: 1_000 + capitalLevel * 500 + warehouseLevel * 2_000,
    stone: 1_000 + capitalLevel * 500 + warehouseLevel * 2_000,
    coins: 1_000 + capitalLevel * 1_000,
  };

  const elapsedHours = Math.min(24, Math.max(0, nowMs - Date.parse(city.lastCollectedAt)) / 3_600_000);
  const claimable = mapWallet(productionPerHour, (rate, resource) =>
    Math.max(
      0,
      Math.min(
        (city.claimable[resource] ?? 0) + Math.floor(rate * elapsedHours),
        storageCaps[resource] - resources[resource],
      ),
    ),
  );

  return { productionPerHour, claimable, storageCaps };
}

export function mapWallet(
  wallet: ResourceWallet,
  mapper: (amount: number, resource: ResourceType) => number,
): ResourceWallet {
  return {
    food: mapper(wallet.food, "food"),
    wood: mapper(wallet.wood, "wood"),
    stone: mapper(wallet.stone, "stone"),
    coins: mapper(wallet.coins, "coins"),
  };
}

export function hasResources(resources: ResourceWallet, cost: ResourceWallet): boolean {
  return resourceTypes().every((resource) => resources[resource] >= cost[resource]);
}

export function subtractWallet(resources: ResourceWallet, cost: ResourceWallet): ResourceWallet {
  return mapWallet(resources, (amount, resource) => amount - cost[resource]);
}

export function addWallet(
  resources: ResourceWallet,
  gain: ResourceWallet,
  caps?: ResourceWallet,
): ResourceWallet {
  return mapWallet(resources, (amount, resource) =>
    Math.min(caps?.[resource] ?? Number.MAX_SAFE_INTEGER, amount + gain[resource]),
  );
}

export function resourceTypes(): ResourceType[] {
  return ["food", "wood", "stone", "coins"];
}

const RESOURCE_ROTATION: ResourceType[] = ["food", "wood", "stone", "food", "wood", "coins"];

export function getTerrain(x: number, y: number): TerrainType {
  const value = Math.abs(x * 17 + y * 29 + x * y * 3) % 13;
  if (value === 0) return "water";
  if (value <= 4) return "forest";
  if (value <= 7) return "highland";
  return "grassland";
}

export function getTileResource(x: number, y: number): ResourceType {
  const terrain = getTerrain(x, y);
  if (terrain === "forest") return "wood";
  if (terrain === "highland") return "stone";
  return RESOURCE_ROTATION[Math.abs(x * 3 + y * 5) % RESOURCE_ROTATION.length] ?? "food";
}

export function getTileLevel(x: number, y: number): number {
  return 1 + (Math.abs(x * 7 + y * 11) % 4);
}

export function getTileDefense(level: number): number {
  return level * 2;
}

export function getTileProduction(resource: ResourceType, level: number): number {
  const base: ResourceWallet = { food: 45, wood: 42, stone: 34, coins: 10 };
  return base[resource] * level;
}

export function getNeutralUnit(x: number, y: number, nodeType: "resource" | "ruins", terrain: TerrainType): NeutralUnitType | null {
  if (terrain === "water") return null;
  const value = Math.abs(x * 43 + y * 71 + x * y * 11);
  if (nodeType === "ruins") return (["goat", "tiger", "rat"] as NeutralUnitType[])[value % 3] ?? "goat";
  if (value % 7 === 0) return "hedgehog";
  if (value % 5 === 0) return "bandits";
  return null;
}

export function calculateArmyPower(units: UnitRoster): number {
  return (Object.entries(units) as [UnitType, number][]).reduce(
    (total, [unitType, quantity]) => total + UNIT_DEFINITIONS[unitType].power * quantity,
    0,
  );
}

export function rosterFromFormation(formation: FormationSlotDto[]): UnitRoster {
  const roster: UnitRoster = { militia: 0, archer: 0, cavalry: 0, shield: 0, crossbow: 0 };
  for (const slot of formation) roster[slot.unitType] += slot.quantity;
  return roster;
}

export function createDefaultCombatPlan(units: UnitRoster): CombatPlanDto {
  const formation: FormationSlotDto[] = [];
  for (const unitType of ["archer", "crossbow", "militia", "shield", "cavalry"] as UnitType[]) {
    let remaining = units[unitType] ?? 0;
    while (remaining > 0 && formation.length < 5) {
      const quantity = Math.min(9, remaining);
      formation.push({ unitType, quantity });
      remaining -= quantity;
    }
  }
  return { formation, tactic: "balanced", heroId: "sun-wu" };
}

function tacticMultiplier(plan: CombatPlanDto): number {
  const roster = rosterFromFormation(plan.formation);
  const total = Math.max(1, Object.values(roster).reduce((sum, amount) => sum + amount, 0));
  const rangedShare = (roster.archer + roster.crossbow) / total;
  const frontlineShare = (roster.militia + roster.shield) / total;
  const cavalryShare = roster.cavalry / total;
  if (plan.tactic === "ranged_volley") return 1 + rangedShare * 0.25;
  if (plan.tactic === "shield_wall") return 1 + frontlineShare * 0.22;
  if (plan.tactic === "cavalry_charge") return 1 + cavalryShare * 0.32;
  return 1.03;
}

function formationOrderMultiplier(plan: CombatPlanDto, neutralUnit: NeutralUnitType | null): number {
  const first = plan.formation[0]?.unitType;
  const last = plan.formation.at(-1)?.unitType;
  const firstRole = first ? UNIT_DEFINITIONS[first].role : undefined;
  const lastRole = last ? UNIT_DEFINITIONS[last].role : undefined;
  if (neutralUnit === "goat" && plan.formation.some((slot) => slot.unitType === "shield")) return 0.82;
  if ((neutralUnit === "tiger" || neutralUnit === "rat") && firstRole === "frontline") return 1.14;
  if ((!neutralUnit || neutralUnit === "bandits" || neutralUnit === "hedgehog") && firstRole === "ranged" && lastRole === "frontline") return 1.12;
  return 1;
}

export function calculateCombatPower(
  plan: CombatPlanDto,
  technology: TechnologyLevels = EMPTY_TECHNOLOGY,
  neutralUnit: NeutralUnitType | null = null,
): number {
  const base = plan.formation.reduce(
    (total, slot) => total + UNIT_DEFINITIONS[slot.unitType].power * slot.quantity,
    0,
  );
  const hero = HERO_DEFINITIONS[plan.heroId];
  const favoredQuantity = plan.formation
    .filter((slot) => hero.favoredRole === "all" || UNIT_DEFINITIONS[slot.unitType].role === hero.favoredRole)
    .reduce((sum, slot) => sum + slot.quantity, 0);
  const totalQuantity = Math.max(1, plan.formation.reduce((sum, slot) => sum + slot.quantity, 0));
  const heroMultiplier = 1 + (hero.multiplier - 1) * favoredQuantity / totalQuantity;
  const researchMultiplier = 1 + (technology.battle_drill ?? 0) * 0.1;
  return Math.max(0, Math.round(base * tacticMultiplier(plan) * heroMultiplier * researchMultiplier * formationOrderMultiplier(plan, neutralUnit)));
}

export function isQuestComplete(player: PlayerStateDto, questId: QuestId): boolean {
  const buildings = player.city?.buildings ?? [];
  if (questId === "raise_barracks") return getBuildingLevel(buildings, "barracks") >= 1;
  if (questId === "claim_three") return player.territoryCount >= 3;
  if (questId === "found_academy") return getBuildingLevel(buildings, "academy") >= 1;
  if (questId === "first_research") return Object.values(player.technology).some((level) => level >= 1);
  return player.territoryCount >= 6;
}

export function getCaptureReward(tile: TileDto): Partial<ResourceWallet> {
  const reward: Partial<ResourceWallet> = { [tile.resourceType]: tile.level * 75 };
  if (tile.neutralUnit) reward.coins = (reward.coins ?? 0) + tile.level * (tile.nodeType === "ruins" ? 35 : 15);
  return reward;
}

export function isAdjacentToOwnedTile(tiles: TileDto[], playerId: string, x: number, y: number): boolean {
  return tiles.some(
    (tile) => tile.ownerId === playerId && Math.abs(tile.x - x) + Math.abs(tile.y - y) === 1,
  );
}

function battleHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export function simulateBattle(
  battleId: string,
  units: UnitRoster,
  tile: TileDto,
  suppliedPlan?: CombatPlanDto,
  technology: TechnologyLevels = EMPTY_TECHNOLOGY,
): BattleReplayDto {
  const plan = suppliedPlan ?? createDefaultCombatPlan(units);
  const attackerPower = calculateCombatPower(plan, technology, tile.neutralUnit);
  const defenderPower = tile.defense;
  const winner = attackerPower >= defenderPower ? "attacker" : "defender";
  const seed = battleHash(`${battleId}:${tile.x}:${tile.y}:${attackerPower}:${defenderPower}:${plan.tactic}:${plan.heroId}`);
  const actors: BattleActorDto[] = plan.formation.map((slot, index) => {
    const definition = UNIT_DEFINITIONS[slot.unitType];
    return {
      id: `attacker:${index}:${slot.unitType}`,
      side: "attacker",
      type: slot.unitType,
      label: definition.name,
      quantity: slot.quantity,
      maxHp: slot.quantity * definition.hp,
      attack: slot.quantity * definition.attack,
      lane: index - (plan.formation.length - 1) / 2,
      role: definition.role,
      range: definition.range,
      speed: definition.speed,
    };
  });
  const neutralMeta: Record<NeutralUnitType, { label: string; role: UnitRole; range: number; speed: number }> = {
    bandits: { label: "กองโจรชายแดน", role: "frontline", range: 1, speed: 5 },
    hedgehog: { label: "ฝูงเม่นสะท้อนคม", role: "frontline", range: 1, speed: 3 },
    goat: { label: "ราชาแพะผา", role: "frontline", range: 2, speed: 5 },
    tiger: { label: "พยัคฆ์เหงียน", role: "cavalry", range: 1, speed: 8 },
    rat: { label: "ราชาหนูท่อ", role: "frontline", range: 2, speed: 7 },
  };
  const neutral = tile.neutralUnit ? neutralMeta[tile.neutralUnit] : null;
  const defender: BattleActorDto = {
    id: `defender:${tile.neutralUnit ?? "garrison"}`,
    side: "defender",
    type: tile.neutralUnit ?? "garrison",
    label: neutral?.label ?? (tile.nodeType === "ruins" ? "ผู้พิทักษ์โบราณ" : "กองรักษาการณ์"),
    quantity: Math.max(1, Math.ceil(defenderPower / 2)),
    maxHp: Math.max(36, defenderPower * 20),
    attack: Math.max(2, defenderPower),
    lane: 0,
    role: neutral?.role ?? "frontline",
    range: neutral?.range ?? 1,
    speed: neutral?.speed ?? 4,
  };
  actors.push(defender);

  const events: BattleEventDto[] = [];
  for (const actor of actors) events.push({ tick: 120, type: "move", actorId: actor.id });
  const attackerHp = actors
    .filter((actor) => actor.side === "attacker")
    .reduce((total, actor) => total + actor.maxHp, 0);
  const attackerTargets = actors.filter((actor) => actor.side === "attacker");
  const rounds = 4;

  for (let round = 0; round < rounds; round += 1) {
    const attackTick = 1_250 + round * 900;
    for (let index = 0; index < attackerTargets.length; index += 1) {
      const actor = attackerTargets[index];
      if (!actor) continue;
      const jitter = 0.88 + ((seed >>> ((round + index) % 16)) & 7) / 25;
      const targetDamage = winner === "attacker"
        ? Math.ceil(defender.maxHp / rounds / Math.max(1, attackerTargets.length))
        : Math.max(1, Math.floor(actor.attack * jitter * 0.45));
      const initiative = actor.role === "ranged" ? -220 : actor.role === "cavalry" ? -80 : 0;
      events.push({ tick: attackTick + initiative + index * 55, type: "attack", actorId: actor.id, targetId: defender.id });
      events.push({ tick: attackTick + initiative + 170 + index * 55, type: "hit", actorId: actor.id, targetId: defender.id, damage: targetDamage });
    }

    const target = attackerTargets[(seed + round) % attackerTargets.length];
    if (target) {
      const defenderDamage = winner === "defender"
        ? Math.ceil(attackerHp / rounds)
        : Math.max(1, Math.ceil(defender.attack * 0.65));
      events.push({ tick: attackTick + 320, type: "attack", actorId: defender.id, targetId: target.id });
      events.push({ tick: attackTick + 480, type: "hit", actorId: defender.id, targetId: target.id, damage: defenderDamage });
      if (tile.neutralUnit === "hedgehog") {
        events.push({ tick: attackTick + 540, type: "skill", actorId: defender.id, targetId: target.id, label: "หนามสะท้อน" });
        events.push({ tick: attackTick + 650, type: "hit", actorId: defender.id, targetId: target.id, damage: Math.max(1, Math.ceil(defenderDamage * 0.35)) });
      }
    }

    if (round === 1 && attackerTargets[0]) {
      const hero = HERO_DEFINITIONS[plan.heroId];
      const skillDamage = Math.max(2, Math.ceil(attackerPower * 0.6));
      events.push({ tick: attackTick + 20, type: "skill", actorId: attackerTargets[0].id, targetId: defender.id, label: `${hero.name} · ${hero.skillName}` });
      events.push({ tick: attackTick + 210, type: "hit", actorId: attackerTargets[0].id, targetId: defender.id, damage: skillDamage });
    }
  }

  const finalTick = 5_100;
  if (winner === "attacker") {
    events.push({ tick: finalTick, type: "defeat", actorId: defender.id });
    events.push({ tick: finalTick + 240, type: "victory", actorId: attackerTargets[0]?.id ?? "attacker:0:militia" });
  } else {
    for (const actor of attackerTargets) events.push({ tick: finalTick, type: "defeat", actorId: actor.id });
    events.push({ tick: finalTick + 240, type: "victory", actorId: defender.id });
  }

  return {
    id: battleId,
    seed,
    winner,
    durationMs: 6_000,
    attackerPower,
    defenderPower,
    terrain: tile.terrain,
    tactic: plan.tactic,
    heroId: plan.heroId,
    heroSkillName: HERO_DEFINITIONS[plan.heroId].skillName,
    actors,
    events,
  };
}
